// Custom Dashboard — a single global date/date-range picker (defaulting to
// Today when nothing is selected) drives every KPI, chart, and ranking below
// via ONE combined GraphQL round trip (getCustomDashboardDataAction), so no
// widget can ever show a different period than the one selected at the top.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { DateRange } from "react-day-picker";
import { ColumnDef } from "@tanstack/react-table";
import {
  CreditCard,
  IndianRupee,
  Receipt,
  Percent,
  Users,
  UserPlus,
  Undo2,
  Package,
  AlertCircle,
  Wallet,
  X,
} from "lucide-react";
import { getCustomDashboardDataAction, categoryAndSubCategoryListAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Skeleton } from "ui/skeleton";
import { Button } from "ui/button";
import { MultiCombobox } from "ui/multi-combobox";
import { DateRangePicker } from "../DateRangePicker";
import { ChartCard } from "./ChartCard";
import { cn } from "lib/utils";
import { CategoryDonutChart } from "./CategoryDonutChart";
import { SalesShareDonutChart } from "./SalesShareDonutChart";
import { CategoryBarChart } from "./CategoryBarChart";
import { TrendAreaChart } from "./TrendAreaChart";
import { CustomSalesTrendChart, SalesTrendGranularity } from "./CustomSalesTrendChart";

const inr = (v: any) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);

// The API layer returns error cases as a "Something went wrong ..." string
// (existing convention across every resolver in this codebase) instead of
// throwing, so a widget with bad data is a string where an object/array was
// expected — that's the one signal available to tell a failed widget apart
// from a merely-empty one.
const isWidgetError = (v: any) => typeof v === "string";

function WidgetError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-3 text-center">
      <AlertCircle className="h-6 w-6 text-destructive" />
      <p className="text-sm font-medium text-foreground">Unable to load data</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

// Small trend indicator for the New Customers KPI — deliberately tiny, no
// axes/tooltip, just a shape showing whether new-customer acquisition is
// trending up or down across the selected period.
function Sparkline({ data }: { data: { date: string; count: number }[] }) {
  if (!data.length) return null;
  return (
    <div className="h-10 w-24">
      <TrendAreaChart data={data.map((d) => ({ date: d.date, value: d.count }))} />
    </div>
  );
}

const TOP_PRODUCT_SORTS: { value: "quantity" | "sales"; label: string }[] = [
  { value: "quantity", label: "Quantity" },
  { value: "sales", label: "Sales" },
];

const TOP_CUSTOMER_SORTS: { value: "orders" | "sales"; label: string }[] = [
  { value: "orders", label: "Orders" },
  { value: "sales", label: "Sales" },
];

const RANKINGS_PAGE_SIZE = 10;
const RANKINGS_TABS = [
  { key: "products", label: "Top Selling Products" },
  { key: "customers", label: "Top Customers" },
] as const;
type RankingsTabKey = (typeof RANKINGS_TABS)[number]["key"];

const CustomDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getCustomDashboardData, getCustomDashboardDataLoad, categoryAndSubCategoryData } = apparelReducerState;

  const [rangeValue, setRangeValue] = React.useState<DateRange | undefined>(undefined);
  const [sortBy, setSortBy] = React.useState<"quantity" | "sales">("quantity");
  const [customerSortBy, setCustomerSortBy] = React.useState<"orders" | "sales">("sales");
  const [categoryIds, setCategoryIds] = React.useState<string[]>([]);
  const [subCategoryIds, setSubCategoryIds] = React.useState<string[]>([]);
  const [rankingsTab, setRankingsTab] = React.useState<RankingsTabKey>("products");
  const [productsPage, setProductsPage] = React.useState(1);
  const [customersPage, setCustomersPage] = React.useState(1);

  const categoryList = categoryAndSubCategoryData?.getCategoryList || [];
  const categoryOptions = React.useMemo(
    () => categoryList.map((c: any) => ({ value: String(c?.id), label: c?.categoryName })),
    [categoryList]
  );
  // Union of sub-categories across every selected category — a sub-category
  // stays selectable/selected as long as its parent category is still checked.
  const subCategoryOptions = React.useMemo(() => {
    const selectedCategories = categoryList.filter((c: any) => categoryIds.includes(String(c?.id)));
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const category of selectedCategories) {
      for (const s of category?.subCategories || []) {
        const value = String(s?.id);
        if (!seen.has(value)) {
          seen.add(value);
          options.push({ value, label: s?.subCategoryName });
        }
      }
    }
    return options;
  }, [categoryList, categoryIds]);

  const topProductsColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productDetailId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId } })} className="text-primary hover:underline text-left">
          {row.original.productName || "-"}
        </button>
      ) : (row.original?.productName || "-") },
      { id: "quantitySold", header: "Quantity Sold", cell: ({ row }) => <span className="tabular-nums">{num(row.original?.quantitySold)}</span> },
      { id: "amount", header: "Sales Amount", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.amount)}</span> },
    ],
    [navigate]
  );

  const topCustomersColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "customerName", header: "Customer", cell: ({ row }) => row.original?.customerDetailId ? (
        <button type="button" onClick={() => navigate("/customer", { state: { auditRecordId: row.original.customerDetailId, auditSearchCode: row.original.mobileNumber } })} className="text-primary hover:underline text-left">
          {row.original.customerName || "-"}
        </button>
      ) : (row.original?.customerName || "-") },
      { id: "mobileNumber", header: "Mobile Number", cell: ({ row }) => row.original?.mobileNumber || "-" },
      { id: "orders", header: "Orders", cell: ({ row }) => <span className="tabular-nums">{num(row.original?.orders)}</span> },
      { id: "sales", header: "Sales", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.sales)}</span> },
      { id: "aov", header: "Avg Order Value", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.aov)}</span> },
    ],
    [navigate]
  );

  const fetchDashboard = React.useCallback(
    (range?: DateRange, sort?: "quantity" | "sales", selectedCategoryIds?: string[], selectedSubCategoryIds?: string[], custSort?: "orders" | "sales") => {
      dispatch(
        getCustomDashboardDataAction({
          fromDate: range?.from ? moment(range.from).format("YYYY-MM-DD") : "",
          toDate: range?.to ? moment(range.to).format("YYYY-MM-DD") : range?.from ? moment(range.from).format("YYYY-MM-DD") : "",
          sortBy: sort ?? sortBy,
          customerSortBy: custSort ?? customerSortBy,
          categoryId: selectedCategoryIds ?? categoryIds,
          subCategoryId: selectedSubCategoryIds ?? subCategoryIds,
        })
      );
    },
    [dispatch, sortBy, customerSortBy, categoryIds, subCategoryIds]
  );

  React.useEffect(() => {
    dispatch(categoryAndSubCategoryListAction({}));
    fetchDashboard(rangeValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRangeChange = (range: DateRange | undefined) => {
    setRangeValue(range);
    if (!range || range.from) fetchDashboard(range);
  };

  const onSortByChange = (sort: "quantity" | "sales") => {
    setSortBy(sort);
    fetchDashboard(rangeValue, sort);
  };

  const onCustomerSortByChange = (sort: "orders" | "sales") => {
    setCustomerSortBy(sort);
    fetchDashboard(rangeValue, sortBy, categoryIds, subCategoryIds, sort);
  };

  const onCategoryChange = (nextCategoryIds: string[]) => {
    setCategoryIds(nextCategoryIds);
    // Drop any sub-category whose parent category was just unchecked.
    const selectedCategories = categoryList.filter((c: any) => nextCategoryIds.includes(String(c?.id)));
    const stillValidSubCategoryIds = new Set(
      selectedCategories.flatMap((c: any) => (c?.subCategories || []).map((s: any) => String(s?.id)))
    );
    const nextSubCategoryIds = subCategoryIds.filter((id) => stillValidSubCategoryIds.has(id));
    setSubCategoryIds(nextSubCategoryIds);
    fetchDashboard(rangeValue, sortBy, nextCategoryIds, nextSubCategoryIds);
  };

  const onSubCategoryChange = (nextSubCategoryIds: string[]) => {
    setSubCategoryIds(nextSubCategoryIds);
    fetchDashboard(rangeValue, sortBy, categoryIds, nextSubCategoryIds);
  };

  const retry = () => fetchDashboard(rangeValue);

  const data = getCustomDashboardData;
  const summaryRaw = data?.getCustomDashboardSummary;
  const loading = getCustomDashboardDataLoad && !data;
  const summary = isWidgetError(summaryRaw) ? null : summaryRaw;

  const trendRaw = data?.getCustomSalesTrend;
  const salesTrend = isWidgetError(trendRaw) ? null : trendRaw;

  const categoryRaw = data?.getCustomSalesByCategory;
  const salesByCategory = isWidgetError(categoryRaw)
    ? null
    : (categoryRaw || []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));

  const subCategoryRaw = data?.getCustomSalesBySubCategory;
  const salesBySubCategory = isWidgetError(subCategoryRaw)
    ? null
    : (Array.isArray(subCategoryRaw) ? subCategoryRaw : []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));

  const growthRaw = data?.getCustomCustomerGrowth;
  const customerGrowth = isWidgetError(growthRaw)
    ? null
    : (growthRaw || []).map((r: any) => ({ date: r.date, value: Number(r.newCustomers) || 0 }));

  const paymentRaw = data?.getCustomPaymentMethodDistribution;
  const paymentMethodSplit = isWidgetError(paymentRaw)
    ? null
    : (paymentRaw || []).map((r: any) => ({ category: r.paymentMethod ?? "Unknown", amount: Number(r.amount) || 0 }));

  const employeeRaw = data?.getCustomSalesByEmployee;
  const salesByEmployee = isWidgetError(employeeRaw)
    ? null
    : (employeeRaw || []).map((r: any) => ({ label: r.employeeName ?? "Unknown", value: Number(r.sales) || 0 }));

  const productsRaw = data?.getCustomTopSellingProducts;
  // The table below shows every product from productsRaw; the chart stays a
  // "top 10" view of the same (already sorted server-side) list.
  const topProducts = isWidgetError(productsRaw)
    ? null
    : (productsRaw || []).slice(0, 10).map((r: any) => ({
        label: r.productName,
        value: sortBy === "sales" ? Number(r.amount) || 0 : Number(r.quantitySold) || 0,
      }));

  const newCustomersRaw = data?.getCustomNewCustomers;
  const newCustomers = isWidgetError(newCustomersRaw) ? null : newCustomersRaw;

  const customersRaw = data?.getCustomTopCustomers;
  // The table below shows every customer from customersRaw; the chart stays a
  // "top 10" view of the same (already sorted server-side) list.
  const topCustomers = isWidgetError(customersRaw)
    ? null
    : (customersRaw || []).slice(0, 10).map((r: any) => ({ label: r.customerName, value: Number(r.sales) || 0 }));

  const salesReturnRaw = data?.getCustomSalesReturn;
  const salesReturn = isWidgetError(salesReturnRaw) ? null : salesReturnRaw;
  const salesReturnTrend = (salesReturn?.trend || []).map((r: any) => ({ date: r.date, value: Number(r.amount) || 0 }));
  const changeCaption = (pct: any) =>
    pct === null || pct === undefined ? "No previous-period data" : `${pct >= 0 ? "+" : ""}${Number(pct).toFixed(1)}% vs previous period`;

  const repeatVsNewRaw = data?.getCustomRepeatVsNewCustomers;
  const repeatVsNew = isWidgetError(repeatVsNewRaw)
    ? null
    : (repeatVsNewRaw || []).map((r: any) => ({ category: r.category, amount: Number(r.amount) || 0 }));

  const allProducts = isWidgetError(productsRaw) ? [] : productsRaw || [];
  const allCustomers = isWidgetError(customersRaw) ? [] : customersRaw || [];
  const pagedProducts = allProducts.slice((productsPage - 1) * RANKINGS_PAGE_SIZE, productsPage * RANKINGS_PAGE_SIZE);
  const pagedCustomers = allCustomers.slice((customersPage - 1) * RANKINGS_PAGE_SIZE, customersPage * RANKINGS_PAGE_SIZE);

  const productsCsvRows = React.useMemo(
    () => allProducts.map((r: any) => ({ ...r, amount: Math.round(Number(r.amount) || 0) })),
    [allProducts]
  );
  const customersCsvRows = React.useMemo(
    () => allCustomers.map((r: any) => ({ ...r, sales: Math.round(Number(r.sales) || 0), aov: Math.round(Number(r.aov) || 0) })),
    [allCustomers]
  );

  // A fresh fetch (new date range/category filter) can shrink the list below
  // whatever page was previously selected — reset both back to page 1 so the
  // table never renders empty from being stranded past the new last page.
  React.useEffect(() => {
    setProductsPage(1);
    setCustomersPage(1);
  }, [data]);

  const periodLabel =
    rangeValue?.from && rangeValue?.to && moment(rangeValue.from).format("YYYY-MM-DD") !== moment(rangeValue.to).format("YYYY-MM-DD")
      ? `${moment(rangeValue.from).format("DD MMM YYYY")} – ${moment(rangeValue.to).format("DD MMM YYYY")}`
      : rangeValue?.from
      ? moment(rangeValue.from).format("DD MMM YYYY")
      : `Today, ${moment().format("DD MMM YYYY")}`;

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          Custom Dashboard
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:flex-nowrap lg:items-center lg:justify-between">
        <div className="min-w-0 shrink-0">
          <p className="text-sm font-medium text-foreground">Selected period: {periodLabel}</p>
          <p className="text-xs text-muted-foreground">
            {rangeValue?.from ? "Showing data for the selected date range." : "No date selected — showing today's data."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:flex-nowrap">
          <MultiCombobox
            options={categoryOptions}
            value={categoryIds}
            onValueChange={onCategoryChange}
            placeholder="All Categories"
            searchPlaceholder="Search category..."
            className="w-[180px]"
          />
          <MultiCombobox
            options={subCategoryOptions}
            value={subCategoryIds}
            onValueChange={onSubCategoryChange}
            placeholder="All Sub-Categories"
            searchPlaceholder="Search sub-category..."
            disabled={categoryIds.length === 0}
            className="w-[180px]"
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Clear category filter"
            className={cn(categoryIds.length === 0 && subCategoryIds.length === 0 && "invisible")}
            onClick={() => {
              setCategoryIds([]);
              setSubCategoryIds([]);
              fetchDashboard(rangeValue, sortBy, [], []);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
          <DateRangePicker value={rangeValue} onChange={onRangeChange} disableFuture />
        </div>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : !summary ? (
          <div className="col-span-full">
            <WidgetError onRetry={retry} />
          </div>
        ) : (
          <>
            <StatCard label="Sales" value={inr(summary?.sales)} icon={IndianRupee} valueClassName="text-xl !whitespace-nowrap" />
            <StatCard label="Orders" value={num(summary?.orders)} icon={Receipt} />
            <StatCard label="Average Order Value" value={inr(summary?.aov)} icon={IndianRupee} valueClassName="text-xl !whitespace-nowrap" />
            <StatCard label="Customers" value={num(summary?.customers)} icon={Users} />
            <StatCard label="Discount Given" value={inr(summary?.discount)} icon={Percent} valueClassName="text-xl !whitespace-nowrap" />
            <StatCard
              label="Sales Return"
              value={inr(salesReturn?.amount)}
              icon={Undo2}
              // A rise in returns is bad news, so up = destructive tint.
              trend={salesReturn?.amountChangePct > 0 ? "down" : salesReturn?.amountChangePct < 0 ? "up" : "neutral"}
              // caption={`${num(salesReturn?.quantity)} items · ${Number(salesReturn?.returnRatePct || 0).toFixed(1)}% of sales`}
              valueClassName="text-xl !whitespace-nowrap"
            />
            <StatCard label="Total Items Sold" value={num(summary?.itemsSold)} icon={Package} valueClassName="text-xl !whitespace-nowrap" />
            {/* More credit given = more owed to us, so up = destructive tint. */}
            <StatCard
              label="Credit Given"
              value={inr(summary?.creditGiven)}
              icon={CreditCard}
              trend={summary?.creditGivenChangePct > 0 ? "down" : summary?.creditGivenChangePct < 0 ? "up" : "neutral"}
              valueClassName="text-xl !whitespace-nowrap"
            />
            <StatCard
              label="Credit Money Recd."
              value={inr(summary?.creditReceived)}
              icon={Wallet}
              trend={summary?.creditReceivedChangePct > 0 ? "up" : summary?.creditReceivedChangePct < 0 ? "down" : "neutral"}
              valueClassName="text-xl !whitespace-nowrap"
            />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Sales Trend" loading={getCustomDashboardDataLoad && !data} isEmpty={!salesTrend?.data?.length}>
          {isWidgetError(trendRaw) ? (
            <WidgetError onRetry={retry} />
          ) : (
            <CustomSalesTrendChart
              data={salesTrend?.data || []}
              granularity={(salesTrend?.granularity as SalesTrendGranularity) || "daily"}
              valueFormatter={inr}
            />
          )}
        </ChartCard>

        <ChartCard title="Sales by Category" bodyClassName="h-auto sm:h-64" loading={getCustomDashboardDataLoad && !data} isEmpty={!salesByCategory?.length}>
          {isWidgetError(categoryRaw) ? <WidgetError onRetry={retry} /> : <SalesShareDonutChart data={salesByCategory || []} valueFormatter={inr} />}
        </ChartCard>

        <ChartCard title="Sales by Sub-Category" bodyClassName="h-auto sm:h-64" loading={getCustomDashboardDataLoad && !data} isEmpty={!salesBySubCategory?.length}>
          {isWidgetError(subCategoryRaw) ? <WidgetError onRetry={retry} /> : <SalesShareDonutChart data={salesBySubCategory || []} valueFormatter={inr} />}
        </ChartCard>

        <ChartCard title="Sales Return Trend" loading={getCustomDashboardDataLoad && !data} isEmpty={!salesReturnTrend.length}>
          {isWidgetError(salesReturnRaw) ? <WidgetError onRetry={retry} /> : <TrendAreaChart data={salesReturnTrend} valueFormatter={inr} />}
        </ChartCard>

        <ChartCard title="Customer Growth" loading={getCustomDashboardDataLoad && !data} isEmpty={!customerGrowth?.length}>
          {isWidgetError(growthRaw) ? <WidgetError onRetry={retry} /> : <TrendAreaChart data={customerGrowth || []} valueFormatter={num} />}
        </ChartCard>

        <ChartCard title="Payment Method Distribution" loading={getCustomDashboardDataLoad && !data} isEmpty={!paymentMethodSplit?.length}>
          {isWidgetError(paymentRaw) ? <WidgetError onRetry={retry} /> : <CategoryDonutChart data={paymentMethodSplit || []} valueFormatter={inr} />}
        </ChartCard>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4">
        <ChartCard title="Sales by Employee" loading={getCustomDashboardDataLoad && !data} isEmpty={!salesByEmployee?.length}>
          {isWidgetError(employeeRaw) ? <WidgetError onRetry={retry} /> : <CategoryBarChart data={salesByEmployee || []} valueFormatter={inr} layout="vertical" />}
        </ChartCard>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard
          title={
            <div className="flex w-full items-center justify-between">
              <span>Top Selling Products</span>
              <div className="flex gap-1">
                {TOP_PRODUCT_SORTS.map((s) => (
                  <Button
                    key={s.value}
                    size="sm"
                    variant={sortBy === s.value ? "default" : "outline"}
                    className="h-6 px-2 text-xs"
                    onClick={() => onSortByChange(s.value)}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </div>
          }
          loading={getCustomDashboardDataLoad && !data}
          isEmpty={!topProducts?.length}
        >
          {isWidgetError(productsRaw) ? (
            <WidgetError onRetry={retry} />
          ) : (
            <CategoryBarChart data={topProducts || []} valueFormatter={sortBy === "sales" ? inr : num} layout="vertical" />
          )}
        </ChartCard>

        <ChartCard title="New Customers" loading={getCustomDashboardDataLoad && !data} isEmpty={!newCustomers}>
          {isWidgetError(newCustomersRaw) ? (
            <WidgetError onRetry={retry} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <p className="text-4xl font-semibold tabular-nums text-foreground">{num(newCustomers?.count)}</p>
              <p className="text-xs text-muted-foreground">First-time buyers in the selected period</p>
              <Sparkline data={newCustomers?.trend || []} />
            </div>
          )}
        </ChartCard>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Top Customers" loading={getCustomDashboardDataLoad && !data} isEmpty={!topCustomers?.length}>
          {isWidgetError(customersRaw) ? <WidgetError onRetry={retry} /> : <CategoryBarChart data={topCustomers || []} valueFormatter={inr} layout="vertical" />}
        </ChartCard>

        <ChartCard title="Repeat vs New Customers" loading={getCustomDashboardDataLoad && !data} isEmpty={!repeatVsNew?.length}>
          {isWidgetError(repeatVsNewRaw) ? <WidgetError onRetry={retry} /> : <CategoryDonutChart data={repeatVsNew || []} valueFormatter={num} />}
        </ChartCard>
      </div>

      {/* Full data behind the Top Selling Products / Top Customers charts above —
          the charts above stay as-is; this table just makes every row readable
          without label truncation, paginated instead of dumping the whole list. */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {RANKINGS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setRankingsTab(tab.key)}
            className={
              rankingsTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      {rankingsTab === "products" ? (
        <DataTable
          columns={topProductsColumns}
          data={pagedProducts}
          loading={getCustomDashboardDataLoad && !data}
          csvExport={{ fileName: `top-selling-products_${moment().format("YYYY-MM-DD")}`, rows: productsCsvRows }}
          toolbar={
            // Same "Sort by" pill-toggle design as the Common Dashboard's Top
            // Products control (DashboardScreen.tsx), reused here as-is.
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Sort by</span>
              <div className="inline-flex rounded-full border p-0.5">
                {TOP_PRODUCT_SORTS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => onSortByChange(s.value)}
                    className={`rounded-full px-3 py-1 font-medium transition-colors ${
                      sortBy === s.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          }
          pagination={{
            page: productsPage,
            total: allProducts.length,
            pageSize: RANKINGS_PAGE_SIZE,
            onChange: setProductsPage,
          }}
        />
      ) : (
        <DataTable
          columns={topCustomersColumns}
          data={pagedCustomers}
          loading={getCustomDashboardDataLoad && !data}
          csvExport={{ fileName: `top-customers_${moment().format("YYYY-MM-DD")}`, rows: customersCsvRows }}
          pagination={{
            page: customersPage,
            total: allCustomers.length,
            pageSize: RANKINGS_PAGE_SIZE,
            onChange: setCustomersPage,
          }}
        />
      )}
    </div>
  );
};

export default CustomDashboardScreen;
